import { useEffect } from 'react'
import type { ShapeManagerStateApi } from './use-shape-manager-state'
import type { LiveGestureUpdatesApi } from './use-live-gesture-updates'
import type { GestureCommitApi } from './use-gesture-commit'

/**
 * Lifecycle of the window pointer listeners a gesture installs. The listeners are stable bridges
 * (`handlePointerMoveBridge`/`handlePointerUpBridge`) that forward to refs, so the latest gesture
 * logic is picked up without re-binding; on unmount everything a gesture may have left behind is
 * torn down.
 */
export function useGesturePointerBridge(
  ctx: ShapeManagerStateApi & LiveGestureUpdatesApi & GestureCommitApi
) {
  const {
    handlePointerMove,
    handlePointerMoveBridge,
    handlePointerMoveRef,
    handlePointerUp,
    handlePointerUpBridge,
    handlePointerUpRef,
    rafIdRef,
    savedBodyCursorForMultiMoveRef,
  } = ctx

  // Refresh the bridge targets to latest interaction logic without re-binding window listeners.
  useEffect(() => {
    handlePointerMoveRef.current = handlePointerMove
    handlePointerUpRef.current = handlePointerUp
  }, [handlePointerMove, handlePointerUp, handlePointerMoveRef, handlePointerUpRef])

  useEffect(() => {
    return () => {
      window.removeEventListener('pointermove', handlePointerMoveBridge)
      window.removeEventListener('pointerup', handlePointerUpBridge, true)
      window.removeEventListener('pointercancel', handlePointerUpBridge, true)
      if (rafIdRef.current != null) {
        window.cancelAnimationFrame(rafIdRef.current)
        rafIdRef.current = null
      }

      if (savedBodyCursorForMultiMoveRef.current !== null) {
        document.body.style.cursor = savedBodyCursorForMultiMoveRef.current
        savedBodyCursorForMultiMoveRef.current = null
      }
    }
  }, [handlePointerMoveBridge, handlePointerUpBridge, rafIdRef, savedBodyCursorForMultiMoveRef])
}
